import { useCallback, useEffect, useRef } from "react";
import Hls from "hls.js/light";

export type VideoPlayerKind = "hlsjs" | "native";

/** Fatal errors to ride out before giving up. A stream that is broken rather than unlucky will
 *  re-fire the same fatal error after every recovery, so without a cap the handler spins. */
const MAX_RECOVERY_ATTEMPTS = 3;
/** Two fatal errors this far apart are separate incidents on a flaky connection, not a spin. The
 *  cap counts consecutive failures, so a long episode that recovers cleanly three times over its
 *  run must not be torn down by the fourth unrelated blip. */
const RECOVERY_WINDOW_MS = 20_000;

/**
 * Which machinery will play `url`.
 *
 * Chrome plays HLS natively, but its demuxer rejects any stream whose decode timestamps are not
 * strictly increasing, and some of our episodes are exactly that — they abort with
 * MEDIA_ERR_DECODE under a second in, before and after the video team's re-encode. hls.js re-aligns
 * those timestamps while transmuxing and plays the episode to the end, so HLS goes through it
 * wherever Media Source Extensions exist. Everything else — progressive mp4, and browsers with
 * native HLS but no MSE — stays on the element's own `src`.
 *
 * hls.js cannot play progressive mp4, so the URL test is a requirement and not an optimisation.
 */
export function videoPlayerKind(url: string): VideoPlayerKind {
  return /\.m3u8(?:[?#]|$)/i.test(url) && Hls.isSupported() ? "hlsjs" : "native";
}

/**
 * Attaches `url` to the video element, and hands back the pair that starts and stops fetching it.
 *
 * The two paths commit at different times, because they cost different amounts:
 * - **native**: `src` is set as soon as the URL is known, leaving the element's own `preload`
 *   attribute in charge — the behaviour every caller had before this hook existed.
 * - **hls.js**: nothing exists until `startLoad()`, and `stopLoad()` destroys it again. Each
 *   instance holds its own MediaSource and buffered fragments, and callers that render many videos
 *   at once keep them all mounted, so an instance per video ever shown would accumulate for as long
 *   as the visitor stays.
 *
 * `startLoad()` also declares that the caller wants playback, which is what lets this hook follow
 * hls.js's documented autoplay pattern — call `play()` from `MANIFEST_PARSED` — without restarting a
 * video the caller has since moved away from.
 */
export function useVideoSource(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  url: string | undefined,
  /** Called when playback is abandoned. hls.js tears its own instance down, and the element emits
   *  no `error` for that, so a caller showing a loading state has no other way to learn of it. */
  onFatal?: () => void
) {
  const hlsRef = useRef<Hls | null>(null);
  const wantsPlaybackRef = useRef(false);
  const recoveryAttemptsRef = useRef(0);
  const lastFatalAtRef = useRef(0);

  // Kept in a ref so the effects below do not re-run when a caller passes a new closure.
  const onFatalRef = useRef(onFatal);
  onFatalRef.current = onFatal;

  const destroyPlayer = useCallback(() => {
    hlsRef.current?.destroy();
    hlsRef.current = null;
    recoveryAttemptsRef.current = 0;
  }, []);

  /** Teardown after a failure, as opposed to teardown because the caller moved on. */
  const abandonPlayback = useCallback(() => {
    destroyPlayer();
    onFatalRef.current?.();
  }, [destroyPlayer]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;
    if (videoPlayerKind(url) === "native") video.src = url;

    return () => {
      wantsPlaybackRef.current = false;
      destroyPlayer();
    };
  }, [videoRef, url, destroyPlayer]);

  const startLoad = useCallback(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    wantsPlaybackRef.current = true;
    if (videoPlayerKind(url) === "native") return;
    if (hlsRef.current) return;

    const hls = new Hls();
    hlsRef.current = hls;
    recoveryAttemptsRef.current = 0;

    // hls.js's documented autoplay point. A caller's own play() before this fires is not wasted —
    // the element remembers the request — but the manifest is the first moment playback can
    // actually begin, and re-issuing here covers a request the attach dropped.
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (wantsPlaybackRef.current) void video.play().catch(() => {});
    });

    hls.on(Hls.Events.ERROR, (_, data) => {
      if (!data.fatal) return;

      const now = Date.now();
      if (now - lastFatalAtRef.current > RECOVERY_WINDOW_MS) recoveryAttemptsRef.current = 0;
      lastFatalAtRef.current = now;

      if (recoveryAttemptsRef.current >= MAX_RECOVERY_ATTEMPTS) {
        abandonPlayback();
        return;
      }
      recoveryAttemptsRef.current += 1;
      // Refetch for a dead transfer, rebuild the buffer for a decode failure. Anything else is
      // unrecoverable, and tearing down leaves the poster on screen rather than a frozen frame.
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
      else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
      else abandonPlayback();
    });

    hls.loadSource(url);
    hls.attachMedia(video);
  }, [videoRef, url, destroyPlayer, abandonPlayback]);

  const stopLoad = useCallback(() => {
    wantsPlaybackRef.current = false;
    destroyPlayer();
  }, [destroyPlayer]);

  return { startLoad, stopLoad };
}
